import { computed, type ComputedRef, type Ref } from 'vue';
import type { FacetOption, FacetSelection } from '@/components/ui/data-table';
import type { RecordRow } from '@/types';

export type { RecordRow };

export const DAY_MS = 1000 * 60 * 60 * 24;
export const WEEK_MS = DAY_MS * 7;

/**
 * Facet value standing in for "this row has no value in this column". Location ids are positive, so
 * zero can never collide with a real one, and a facet value has to be a scalar the table can
 * compare — `null` would be indistinguishable from "no filter".
 */
export const BLANK_FACET_VALUE = 0;

export type RecordWeekBin = {
    weekStart: string;
    /** UTC midnight of `weekStart`, the x value Highcharts plots. */
    timestamp: number;
    number: number;
};

export type RecordTotals = {
    totalPigs: number;
    totalWeight: number;
    /** `Sum(total_weight) / Sum(number)` over weight-bearing rows only. Null when there are none. */
    weightedAverageWeight: number | null;
    /** True when at least one row in scope carries no weight, so pigs and weight cannot reconcile. */
    hasRowsWithoutWeight: boolean;
};

/**
 * How one filterable column reads a row: the scalar it filters on, and the name shown for it.
 *
 * The two travel together because the option list and the matching have to agree — an option keyed
 * on a value the filter never produces silently filters everything away. Values are ids rather than
 * names: barn and section names repeat across parents, so comparing names would pull in rows from a
 * farm the user did not pick.
 */
export type RecordFacetDefinition<TRow> = {
    value: (row: TRow) => string | number;
    label: (row: TRow) => string;
};

/** The filterable columns of a page, by column id. */
export type RecordFacetDefinitions<TRow> = Record<string, RecordFacetDefinition<TRow>>;

/**
 * UTC midnight of a `yyyy-MM-dd` day.
 *
 * Split-then-`Date.UTC` rather than parsing: the chart's week label is rendered from the UTC parts
 * of this timestamp, so a value built from a local-midnight `Date` lands in the previous week for
 * anyone west of UTC and shifts the label at every Monday boundary.
 */
export function toUtcTimestamp(dateString: string): number {
    const [year, month, day] = dateString.split('-').map(Number);

    return Date.UTC(year, month - 1, day);
}

/** `yyyy-MM-dd` of a UTC timestamp. Inverse of `toUtcTimestamp`. */
export function toDateString(timestamp: number): string {
    return new Date(timestamp).toISOString().slice(0, 10);
}

/** The closed day interval a week bin covers: its Monday through the following Sunday. */
export function weekInterval(weekStart: string): [string, string] {
    return [weekStart, toDateString(toUtcTimestamp(weekStart) + 6 * DAY_MS)];
}

/**
 * Pigs per week, one point per week from the first populated week to the last.
 *
 * Weeks with no rows are filled in with an explicit zero so the bars stay evenly spaced and every
 * week remains hoverable and clickable. Boundaries are never computed here — each filled week is a
 * whole number of weeks after a `week_start` the server already decided.
 */
export function buildWeeklyBins(rows: RecordRow[]): RecordWeekBin[] {
    const pigsByWeek = new Map<string, number>();

    for (const row of rows) {
        pigsByWeek.set(row.week_start, (pigsByWeek.get(row.week_start) ?? 0) + row.number);
    }

    if (!pigsByWeek.size) {
        return [];
    }

    const timestamps = [...pigsByWeek.keys()].map(toUtcTimestamp);
    const first = Math.min(...timestamps);
    const last = Math.max(...timestamps);

    const bins: RecordWeekBin[] = [];

    for (let timestamp = first; timestamp <= last; timestamp += WEEK_MS) {
        const weekStart = toDateString(timestamp);

        bins.push({ weekStart, timestamp, number: pigsByWeek.get(weekStart) ?? 0 });
    }

    return bins;
}

/** Rows whose week bin is the selected one. A null selection means every row. */
export function filterRowsByWeek<TRow extends RecordRow>(rows: TRow[], weekStart: string | null): TRow[] {
    if (weekStart === null) {
        return rows;
    }

    return rows.filter((row) => row.week_start === weekStart);
}

/** Rows matching every active facet. A column with nothing selected does not narrow anything. */
export function applyFacetFilters<TRow>(rows: TRow[], filters: FacetSelection, definitions: RecordFacetDefinitions<TRow>): TRow[] {
    const active = Object.entries(filters)
        .map(([columnId, selected]) => ({ definition: definitions[columnId], selected }))
        .filter((filter) => filter.definition !== undefined && filter.selected.length > 0);

    if (!active.length) {
        return rows;
    }

    return rows.filter((row) => active.every((filter) => filter.selected.includes(filter.definition.value(row))));
}

/**
 * The option set behind each header facet filter, keyed on id and labelled by name.
 *
 * Built from the whole loaded row set rather than from the rows currently on screen, so applying
 * one filter never erases the options of another — the Excel behaviour, where narrowing a column
 * leaves the remaining choices visible.
 */
export function buildFacetOptions<TRow>(rows: TRow[], definitions: RecordFacetDefinitions<TRow>): Record<string, FacetOption[]> {
    return Object.fromEntries(
        Object.entries(definitions).map(([columnId, definition]) => {
            const options = new Map<string | number, FacetOption>();

            for (const row of rows) {
                const value = definition.value(row);

                if (!options.has(value)) {
                    options.set(value, { value, label: definition.label(row) });
                }
            }

            return [columnId, [...options.values()].sort((a, b) => a.label.localeCompare(b.label))];
        })
    );
}

/**
 * Footer figures over every row handed in — the caller passes the fully filtered set, never a page.
 *
 * A row with no weight still counts its pigs but is excluded from the weight sum and from both
 * sides of the average, so the average stays a true weight-per-pig of the pigs that were weighed
 * rather than the mean of a column of per-row averages.
 */
export function computeTotals(rows: RecordRow[]): RecordTotals {
    let totalPigs = 0;
    let totalWeight = 0;
    let weighedPigs = 0;
    let hasRowsWithoutWeight = false;

    for (const row of rows) {
        totalPigs += row.number;

        if (row.total_weight === null || row.avg_weight === null) {
            hasRowsWithoutWeight = true;
        } else {
            totalWeight += row.total_weight;
            weighedPigs += row.number;
        }
    }

    return {
        totalPigs,
        totalWeight,
        weightedAverageWeight: weighedPigs > 0 ? totalWeight / weighedPigs : null,
        hasRowsWithoutWeight,
    };
}

export type RecordsAggregation<TRow> = {
    bins: ComputedRef<RecordWeekBin[]>;
    facetOptions: ComputedRef<Record<string, FacetOption[]>>;
    visibleRows: ComputedRef<TRow[]>;
    totals: ComputedRef<RecordTotals>;
};

/**
 * Everything a records page derives from its loaded rows: the chart's bins, the header facets'
 * option sets, the rows the filters leave, and the totals over exactly those rows.
 *
 * Filtering runs one way. The chart is the whole loaded period, always, and the table is what the
 * filters leave of it - nothing the table does reaches back into the bars. That keeps the chart a
 * fixed frame to read a narrowed table against, which is the question it is there to answer: how
 * this week compares to the rest of the period. It also means a bar the reader is standing on can
 * never disappear from under them.
 *
 * The trade is that with a facet active a bar is no longer the count the totals show for that week -
 * the bar counts the period, the totals count the selection. The totals state their own figures and
 * the active facets are visible in the header, so the two are readable apart.
 *
 * `facets` is a getter rather than a plain object because its labels are translated strings, and
 * reading them per render is what lets a locale change reach them.
 */
export function useRecordsAggregation<TRow extends RecordRow>(
    rows: Ref<TRow[]>,
    selectedWeekStart: Ref<string | null>,
    columnFilters: Ref<FacetSelection>,
    facets: () => RecordFacetDefinitions<TRow>
): RecordsAggregation<TRow> {
    const bins = computed(() => buildWeeklyBins(rows.value));
    // Options come from the unfiltered rows, so narrowing one column never erases another's choices.
    const facetOptions = computed(() => buildFacetOptions(rows.value, facets()));

    const visibleRows = computed(() => filterRowsByWeek(applyFacetFilters(rows.value, columnFilters.value, facets()), selectedWeekStart.value));
    const totals = computed(() => computeTotals(visibleRows.value));

    return { bins, facetOptions, visibleRows, totals };
}
